import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Search, TrendingUp, Globe, ShoppingBag } from "lucide-react";

const SRC_COLORS = { organic: "#A6FF00", direct: "#00C2D6", referral: "#5EEAD4", paid: "#F59E0B", campaign: "#C084FC" };

export default function AdminSeo() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(28);

  useEffect(() => {
    api.get(`/admin/seo/summary?days=${days}`).then((r) => setData(r.data)).catch(() => {});
  }, [days]);

  if (!data) return <div className="p-8 text-[#9CA8B8]" data-testid="seo-loading">Loading organic traffic…</div>;

  const organicTotal = Object.entries(data.sources).filter(([k]) => k === "organic").reduce((a, [, v]) => a + v, 0);
  const allTotal = Object.values(data.sources).reduce((a, v) => a + v, 0) || 1;
  const maxDay = Math.max(1, ...data.sessions_by_day.map((d) => (d.organic || 0) + (d.direct || 0) + (d.referral || 0) + (d.paid || 0) + (d.campaign || 0)));

  return (
    <div className="space-y-8" data-testid="admin-seo-page">
      <div>
        <h1 className="text-2xl font-bold text-[#F7FAFC]">SEO & Organic Traffic</h1>
        <p className="text-sm text-[#9CA8B8]">Landing sessions by source, organic landing pages and organic-attributed revenue.</p>
      </div>

      <div className="flex gap-2">
        {[7, 28, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)} data-testid={`seo-range-${d}`}
            className={`px-3 py-1.5 text-xs rounded-sm border ${days === d ? "bg-[#A6FF00] text-[#0B1220] border-[#A6FF00] font-bold" : "border-[#00C2D6]/30 text-[#9CA8B8]"}`}>
            {d} days
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat icon={<Search className="w-4 h-4" />} label="Organic sessions" value={organicTotal} testid="seo-stat-organic" />
        <Stat icon={<Globe className="w-4 h-4" />} label="All landing sessions" value={allTotal} testid="seo-stat-all" />
        <Stat icon={<ShoppingBag className="w-4 h-4" />} label="Organic orders" value={data.organic_orders} testid="seo-stat-orders" />
        <Stat icon={<TrendingUp className="w-4 h-4" />} label="Organic revenue" value={`£${data.organic_revenue.toFixed(2)}`} testid="seo-stat-revenue" />
      </div>

      <div className="bg-[#0B1220] border border-[#00C2D6]/15 rounded-sm p-5">
        <h2 className="text-sm font-bold text-[#5EEAD4] uppercase tracking-wider mb-4">Landing sessions by day</h2>
        {data.sessions_by_day.length === 0 ? (
          <p className="text-sm text-[#9CA8B8]" data-testid="seo-no-data">No data yet — tracking starts collecting as soon as visitors land on the site.</p>
        ) : (
          <div className="flex items-end gap-1 h-36" data-testid="seo-chart">
            {data.sessions_by_day.map((d) => {
              const total = (d.organic || 0) + (d.direct || 0) + (d.referral || 0) + (d.paid || 0) + (d.campaign || 0);
              return (
                <div key={d.day} className="flex-1 flex flex-col justify-end gap-px" title={`${d.day}: ${total} sessions (${d.organic || 0} organic)`}>
                  {["campaign", "paid", "referral", "direct", "organic"].map((s) =>
                    d[s] ? <div key={s} style={{ height: `${(d[s] / maxDay) * 100}%`, background: SRC_COLORS[s] }} /> : null
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div className="flex gap-4 mt-3 flex-wrap">
          {Object.entries(SRC_COLORS).map(([s, c]) => (
            <span key={s} className="text-[10px] text-[#9CA8B8] flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 inline-block rounded-sm" style={{ background: c }} /> {s} ({data.sources[s] || 0})
            </span>
          ))}
        </div>
      </div>

      <div className="bg-[#0B1220] border border-[#00C2D6]/15 rounded-sm p-5">
        <h2 className="text-sm font-bold text-[#5EEAD4] uppercase tracking-wider mb-4">Top organic landing pages</h2>
        {data.top_organic_landing_pages.length === 0 ? (
          <p className="text-sm text-[#9CA8B8]">No organic landings recorded yet.</p>
        ) : (
          <table className="w-full text-sm" data-testid="seo-landing-table">
            <tbody>
              {data.top_organic_landing_pages.map((p) => (
                <tr key={p.path} className="border-b border-[#00C2D6]/10">
                  <td className="py-2 text-[#D5DCE8]">{p.path}</td>
                  <td className="py-2 text-right text-[#A6FF00] font-bold">{p.sessions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-[#0B1220] border border-[#A6FF00]/30 rounded-sm p-5 text-sm text-[#9CA8B8]">
        <h2 className="text-sm font-bold text-[#A6FF00] uppercase tracking-wider mb-2">Level up: Google Search Console</h2>
        <p>For keyword-level rankings (which searches show us, our position, and click-through), connect the site to
        Google Search Console — it's free and takes 5 minutes. Once verified you'll see the exact queries we rank for.</p>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, testid }) {
  return (
    <div className="bg-[#0B1220] border border-[#00C2D6]/15 rounded-sm p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[#5EEAD4] text-[10px] uppercase tracking-wider mb-1">{icon}{label}</div>
      <div className="text-2xl font-extrabold text-[#F7FAFC]">{value}</div>
    </div>
  );
}
